// Phase 1C results pane: selects the most-recent COMPLETE Run on
// the active Test and shows:
//   - LF damper-pos predicted (and recorded if available) chart
//   - Per-corner R²_dyn + Δmean numbers (one line per corner)
//   - Per-input provenance chips (PASS/FALLBACK/ABSENT)
//
// "Most-recent complete" = the highest run_number with status='complete'.
// If no complete Run exists yet (everything pending/running/failed/cancelled),
// the pane shows a clear empty state.
import { useEffect, useMemo, useState } from "react";

import type { Run, Test } from "../api/tests";
import {
  fetchKpiSummary,
  fetchWaveformJson,
  type KpiSummary,
  type WaveformJson,
} from "../api/runs";
import { DamperSettingProvenanceChips } from "./DamperSettingProvenanceChips";
import { OverlayChart } from "./OverlayChart";
import { buildOverlayData } from "./overlayAdapter";
import { ProvenanceChips } from "./ProvenanceChips";
import { TrackPositionStrip } from "./TrackPositionStrip";
import { FvOperatingPointPanel } from "./FvOperatingPointPanel";

export type ResultsPaneProps = {
  test: Test;
  // When set (and that run is complete), show this run's waveform instead of
  // the most-recent-complete default — lets the engineer click the R0/R1/R2…
  // run cards to inspect each run's waveform after a study finishes. null /
  // undefined preserves the auto "most-recent-complete" behaviour.
  selectedRunId?: number | null;
};

// baselineKpi state:
//   null         — displayed Run IS R0 (no comparison to make)
//   "unavailable" — R0 exists but has no kpi_summary artifact yet
//   KpiSummary   — both Runs have KPIs; render the Δ columns
type BaselineKpiState = KpiSummary | "unavailable" | null;

type ResultState =
  | { kind: "no-complete-run" }
  | { kind: "loading"; runId: number }
  | { kind: "error"; runId: number; message: string }
  | {
      kind: "ready";
      run: Run;
      kpi: KpiSummary;
      waveform: WaveformJson;
      baselineKpi: BaselineKpiState;
    };

function selectMostRecentComplete(runs: Run[]): Run | null {
  const complete = runs.filter((r) => r.status === "complete");
  if (complete.length === 0) return null;
  return complete.reduce(
    (best, r) => (r.run_number > best.run_number ? r : best),
    complete[0],
  );
}

function findR0(runs: Run[]): Run | null {
  return runs.find((r) => r.run_number === 0) ?? null;
}

function findCornerKpi(
  kpi: KpiSummary, corner: string,
): KpiSummary["per_corner"][0] | undefined {
  return kpi.per_corner.find((c) => c.corner.toLowerCase() === corner.toLowerCase());
}

// Signed Δmean = predicted - recorded (in m). Multiply by 1000 for mm.
// Null for track-replay runs (item D): no recorded mean to compare against.
function signedDeltaMeanMm(c: KpiSummary["per_corner"][0]): number | null {
  if (c.recorded_mean_m === null) return null;
  return (c.predicted_mean_m - c.recorded_mean_m) * 1000;
}

function formatDelta(value: number, digits: number): string {
  const s = value.toFixed(digits);
  return value >= 0 ? `+${s}` : s;
}

export function ResultsPane({ test, selectedRunId = null }: ResultsPaneProps) {
  // Derive primitive IDs from test.runs so the fetch effect only
  // re-fires when the actually-displayed Run (or its baseline R0)
  // changes. test.runs is a fresh array on every parent poll —
  // depending on it would refetch the waveform every 2s while a
  // session polls, which is the "odd refresh glitch" Joe flagged.
  //
  // A user-selected run (clicking its card) wins when it's complete;
  // otherwise fall back to the most-recent-complete default.
  const targetId = useMemo(() => {
    if (selectedRunId != null) {
      const sel = test.runs.find(
        (r) => r.id === selectedRunId && r.status === "complete",
      );
      if (sel) return sel.id;
    }
    return selectMostRecentComplete(test.runs)?.id ?? null;
  }, [test.runs, selectedRunId]);
  const r0Id = useMemo(
    () => findR0(test.runs)?.id ?? null,
    [test.runs],
  );
  const [state, setState] = useState<ResultState>(
    targetId === null
      ? { kind: "no-complete-run" }
      : { kind: "loading", runId: targetId },
  );
  // Shared cursor index, lifted from the overlay chart (a member of the
  // "damper-overlay" sync group). Hovering any synced chart moves it, and the
  // F-V operating-point dots ride it.
  const [cursorIdx, setCursorIdx] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (targetId === null) {
      setState({ kind: "no-complete-run" });
      return;
    }
    setState({ kind: "loading", runId: targetId });
    (async () => {
      try {
        const [kpi, waveform] = await Promise.all([
          fetchKpiSummary(targetId),
          fetchWaveformJson(targetId, { maxPoints: 1000 }),
        ]);
        if (cancelled) return;

        // Compute baseline KPI (R0) — only when the displayed Run
        // ISN'T R0 itself. Comparison uses the STORED R0 artifact,
        // not recomputed physics, per the trust-the-artifacts rule.
        let baselineKpi: BaselineKpiState = null;
        if (r0Id !== null && r0Id !== targetId) {
          try {
            baselineKpi = await fetchKpiSummary(r0Id);
          } catch {
            // R0 exists but the kpi_summary artifact is missing /
            // unreachable. Show current KPI only + a quiet hint.
            baselineKpi = "unavailable";
          }
        }
        if (cancelled) return;
        // Snapshot the run object from the current test.runs reference
        // for the rendered state; if it's gone (raced with a delete),
        // bail out — the next render will re-fire with a different
        // targetId.
        const fixedRun = test.runs.find((r) => r.id === targetId);
        if (!fixedRun) return;
        setState({
          kind: "ready", run: fixedRun, kpi, waveform, baselineKpi,
        });
      } catch (e) {
        if (cancelled) return;
        setState({
          kind: "error",
          runId: targetId,
          message: e instanceof Error ? e.message : String(e),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally NOT depending on test.runs — we use it only to
    // re-snapshot the run object at the end of a successful fetch,
    // and re-firing on every reference change is exactly the glitch
    // we're fixing here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetId, r0Id]);

  if (state.kind === "no-complete-run") {
    return (
      <section className="card results-pane" data-testid="results-pane">
        <h4 style={{ marginTop: 0 }}>Results</h4>
        <p className="muted">
          No completed Run yet for this Test. Click <strong>Run reference
          prediction</strong> to enqueue the synth — the chart + KPI numbers +
          provenance chips will populate once it finishes.
        </p>
      </section>
    );
  }

  if (state.kind === "loading") {
    return (
      <section className="card results-pane" data-testid="results-pane">
        <h4 style={{ marginTop: 0 }}>
          Results · R
          {test.runs.find((r) => r.id === state.runId)?.run_number ?? "?"}
        </h4>
        <p className="muted">Loading run results…</p>
      </section>
    );
  }

  if (state.kind === "error") {
    return (
      <section className="card results-pane" data-testid="results-pane">
        <h4 style={{ marginTop: 0 }}>
          Results · R
          {test.runs.find((r) => r.id === state.runId)?.run_number ?? "?"}
        </h4>
        <p style={{ color: "#ff9a9a" }}>
          Failed to load run #{state.runId}: {state.message}
        </p>
      </section>
    );
  }

  const { run, kpi, waveform, baselineKpi } = state;
  const baselineKpiSummary: KpiSummary | null =
    baselineKpi !== null && baselineKpi !== "unavailable" ? baselineKpi : null;
  const overlay = buildOverlayData(waveform);
  // Track-replay runs (item D) have no recorded damper-pos channel, so every
  // per-corner R²_dyn / RMSE / Δmean is null — the same condition that makes
  // the header read "n/a (replay)". Rather than render a wall of dashes (reads
  // as broken), the table below swaps to the simulator's REAL predicted mean.
  const isReplay = kpi.mean_r2_dyn === null;

  return (
    <section className="card results-pane" data-testid="results-pane">
      <h4 style={{ marginTop: 0 }}>
        Results · R{run.run_number} ({run.run_number === 0 ? "Reference" : "Candidate"})
        <span className="muted" style={{ fontSize: "12px", marginLeft: "8px" }}>
          mean R²_dyn = <strong>{kpi.mean_r2_dyn === null ? "n/a (replay)" : kpi.mean_r2_dyn.toFixed(3)}</strong>
        </span>
      </h4>

      <div className="results-grid">
      <div className="results-chart" data-testid="results-chart">
      {overlay.ok ? (
        <OverlayChart
          data={overlay}
          title={`damper-pos · ${waveform.source_event ?? "(unknown event)"}`}
          onCursorIdx={setCursorIdx}
        />
      ) : (
        <div className="muted" data-testid="overlay-empty">
          No chartable data ({overlay.reason})
        </div>
      )}
      <TrackPositionStrip testId={test.id} />
      <FvOperatingPointPanel
        testId={test.id}
        run={run}
        waveform={waveform}
        cursorIdx={cursorIdx}
      />
      </div>

      <div className="results-kpi-dock" data-testid="results-kpi-dock">
      {isReplay ? (
      <>
      <table
        style={{
          width: "100%",
          fontSize: "12px",
          borderCollapse: "collapse",
        }}
        data-testid="kpi-table"
      >
        <thead>
          <tr style={{ textAlign: "left", color: "#9a9a9a" }}>
            <th>corner</th>
            <th>pred mean (mm)</th>
          </tr>
        </thead>
        <tbody>
          {kpi.per_corner.map((c) => {
            // No recorded ground truth on replay — but the predicted mean IS
            // real (the simulator outputs it either way). Surface it, plus a
            // NEUTRAL Δ vs R0 (grey, no good/bad colour: "better" direction
            // for a raw mean position is ambiguous) when a baseline exists.
            const predMm = c.predicted_mean_m * 1000;
            const r0Corner = baselineKpiSummary
              ? findCornerKpi(baselineKpiSummary, c.corner)
              : undefined;
            const predDelta = r0Corner
              ? (c.predicted_mean_m - r0Corner.predicted_mean_m) * 1000
              : null;
            return (
              <tr key={c.corner}>
                <td>{c.corner}</td>
                <td>
                  {predMm.toFixed(2)}
                  {predDelta !== null && (
                    <span
                      data-testid={`kpi-row-${c.corner}-predmean-delta`}
                      style={{
                        marginLeft: "6px",
                        color: "#9a9a9a",
                        fontSize: "11px",
                      }}
                    >
                      ({formatDelta(predDelta, 2)})
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p
        data-testid="replay-no-ground-truth"
        className="muted"
        style={{ fontSize: "11px", marginTop: "4px" }}
      >
        Replay study — no recorded ground truth, so R²_dyn / RMSE / Δmean are
        undefined. Quality is judged by the validity guardrails + the waveform
        above.{baselineKpiSummary && " Δ = predicted mean vs Reference run R0."}
      </p>
      </>
      ) : (
      <>
      <table
        style={{
          width: "100%",
          fontSize: "12px",
          borderCollapse: "collapse",
        }}
        data-testid="kpi-table"
      >
        <thead>
          <tr style={{ textAlign: "left", color: "#9a9a9a" }}>
            <th>corner</th>
            <th>R²_dyn</th>
            <th>RMSE (mm)</th>
            <th>Δmean (mm)</th>
          </tr>
        </thead>
        <tbody>
          {kpi.per_corner.map((c) => {
            const dMean = signedDeltaMeanMm(c);
            const r0Corner = baselineKpiSummary
              ? findCornerKpi(baselineKpiSummary, c.corner)
              : undefined;
            // Color guide for deltas (per-metric direction):
            //   R²_dyn — positive Δ = improvement (green)
            //   RMSE   — negative Δ = improvement (green)
            //   Δmean  — magnitude shrinking = improvement (green); we
            //            colour by |Δ|_now < |Δ|_R0 rather than by sign
            //            of (Δ_now - Δ_R0) so the engineer sees "closer
            //            to truth" highlighted regardless of direction.
            const r0dMean = r0Corner ? signedDeltaMeanMm(r0Corner) : null;
            const r2dynDelta =
              r0Corner && c.r2_dyn !== null && r0Corner.r2_dyn !== null
                ? c.r2_dyn - r0Corner.r2_dyn : null;
            const rmseDelta =
              r0Corner && c.rmse_m !== null && r0Corner.rmse_m !== null
                ? (c.rmse_m - r0Corner.rmse_m) * 1000 : null;
            const dmeanDelta =
              dMean !== null && r0dMean !== null ? dMean - r0dMean : null;
            const dmeanShrink =
              dMean !== null && r0dMean !== null
                ? Math.abs(dMean) < Math.abs(r0dMean) : false;
            const r2dynTone =
              r2dynDelta === null ? "#9a9a9a"
              : r2dynDelta >= 0 ? "#7ed47e" : "#ff9a9a";
            const rmseTone =
              rmseDelta === null ? "#9a9a9a"
              : rmseDelta <= 0 ? "#7ed47e" : "#ff9a9a";
            const dmeanTone =
              dmeanDelta === null ? "#9a9a9a"
              : dmeanShrink ? "#7ed47e" : "#ff9a9a";
            return (
              <tr key={c.corner}>
                <td>{c.corner}</td>
                <td>
                  {c.r2_dyn === null ? "—" : c.r2_dyn.toFixed(3)}
                  {r2dynDelta !== null && (
                    <span
                      data-testid={`kpi-row-${c.corner}-r2dyn-delta`}
                      style={{
                        marginLeft: "6px",
                        color: r2dynTone,
                        fontSize: "11px",
                      }}
                    >
                      ({formatDelta(r2dynDelta, 2)})
                    </span>
                  )}
                </td>
                <td>
                  {c.rmse_m === null ? "—" : (c.rmse_m * 1000).toFixed(2)}
                  {rmseDelta !== null && (
                    <span
                      data-testid={`kpi-row-${c.corner}-rmse-delta`}
                      style={{
                        marginLeft: "6px",
                        color: rmseTone,
                        fontSize: "11px",
                      }}
                    >
                      ({formatDelta(rmseDelta, 2)})
                    </span>
                  )}
                </td>
                <td>
                  {dMean === null ? "—" : dMean.toFixed(2)}
                  {dmeanDelta !== null && (
                    <span
                      data-testid={`kpi-row-${c.corner}-dmean-delta`}
                      style={{
                        marginLeft: "6px",
                        color: dmeanTone,
                        fontSize: "11px",
                      }}
                    >
                      ({formatDelta(dmeanDelta, 2)})
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {baselineKpiSummary && (
        <p className="muted" style={{ fontSize: "11px", marginTop: "4px" }}>
          Δ columns vs Reference run R0. Green = improvement, red = regression.
          Δmean is signed (predicted − recorded); colour reflects whether
          |Δmean| shrank relative to R0.
        </p>
      )}
      {baselineKpi === "unavailable" && (
        <p
          data-testid="baseline-unavailable"
          className="muted"
          style={{ fontSize: "11px", marginTop: "4px" }}
        >
          R0 kpi_summary not yet available — showing this Run's KPIs only.
        </p>
      )}
      </>
      )}

      {run.provenance ? (
        <ProvenanceChips summary={run.provenance} />
      ) : (
        <p className="muted" style={{ fontSize: "12px", marginTop: "8px" }}>
          (no provenance summary on this run — likely an older row before
          provenance surfacing landed)
        </p>
      )}

      <DamperSettingProvenanceChips runId={run.id} />
      </div>
      </div>
    </section>
  );
}
